import React, { useState } from "react";
import { CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  ANNEE_BAREME,
  SOURCE_BAREME,
  TAUX,
  PLAFOND_TRAVAUX,
  plafond,
  categoriePour,
  formaterEuros,
} from "../utils/plafonds";

/**
 * Vérification d'éligibilité en deux champs, pour le haut des pages Ads.
 *
 * Le simulateur complet (/simulateur) demande une dizaine de réponses : c'est
 * trop pour un visiteur qui vient de cliquer sur une annonce et ne connaît pas
 * encore le site. Ici, deux champs suffisent à trancher la seule question qui
 * décide de tout — les ressources — et le résultat amène au rappel.
 *
 * Les plafonds et les taux viennent de utils/plafonds.js, source unique du
 * site : aucun chiffre n'est recopié ici.
 */
const SimulateurRapide = ({ travauxEstimes = null, onEligible = null }) => {
  const [nbPersonnes, setNbPersonnes] = useState("");
  const [revenu, setRevenu] = useState("");
  const [resultat, setResultat] = useState(null);

  const calculer = (e) => {
    e.preventDefault();

    const n = parseInt(nbPersonnes, 10);
    const rfr = parseInt(String(revenu).replace(/[\s.]/g, ""), 10);
    if (!n || Number.isNaN(rfr)) {
      return setResultat({ type: "incomplet" });
    }

    const categorie = categoriePour(rfr, n);
    if (!categorie) {
      return setResultat({
        type: "hors-plafond",
        plafondModestes: plafond("modestes", n),
      });
    }

    // L'aide porte sur le montant des travaux, dans la limite du plafond.
    // Sans estimation de travaux, on annonce le maximum théorique.
    const assiette = travauxEstimes
      ? Math.min(travauxEstimes, PLAFOND_TRAVAUX)
      : PLAFOND_TRAVAUX;
    const taux = TAUX[categorie];

    setResultat({
      type: "eligible",
      categorie,
      taux,
      aide: Math.round(assiette * taux),
      resteACharge: Math.round(assiette * (1 - taux)),
      assiette,
    });

    if (onEligible) onEligible();
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border-2 border-orange-200 p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Vérifiez votre éligibilité en 30 secondes
      </h2>
      <p className="text-gray-700 mb-6">
        Deux informations suffisent. Aucune coordonnée demandée à cette étape.
      </p>

      <form onSubmit={calculer} className="space-y-5">
        <div>
          <label
            htmlFor="sr-personnes"
            className="block text-sm font-semibold text-gray-900 mb-2"
          >
            Combien de personnes vivent dans le logement ?
          </label>
          <select
            id="sr-personnes"
            value={nbPersonnes}
            onChange={(e) => setNbPersonnes(e.target.value)}
            className="w-full min-h-[48px] px-4 border-2 border-gray-300 rounded-lg text-base focus:border-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-200"
            required
          >
            <option value="">Choisissez</option>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n} personne{n > 1 ? "s" : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="sr-revenu"
            className="block text-sm font-semibold text-gray-900 mb-2"
          >
            Revenu fiscal de référence du foyer
          </label>
          <input
            id="sr-revenu"
            type="text"
            inputMode="numeric"
            value={revenu}
            onChange={(e) => setRevenu(e.target.value)}
            placeholder="Par exemple 21 500"
            className="w-full min-h-[48px] px-4 border-2 border-gray-300 rounded-lg text-base focus:border-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-200"
            required
          />
          <p className="text-sm text-gray-700 mt-2">
            Il figure sur votre dernier avis d&apos;imposition, ligne « revenu fiscal
            de référence ». Additionnez ceux de toutes les personnes du logement.
          </p>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full bg-orange-700 hover:bg-orange-800 text-white text-lg min-h-[56px]"
        >
          Voir mon aide estimée
          <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
        </Button>
      </form>

      {/* Le résultat est annoncé aux lecteurs d'écran : il apparaît sous le
          bouton, sans changement de page. */}
      <div role="status" aria-live="polite">
        {resultat?.type === "incomplet" && (
          <p className="mt-6 p-4 bg-gray-100 rounded-lg text-gray-900">
            Merci de renseigner les deux champs.
          </p>
        )}

        {resultat?.type === "hors-plafond" && (
          <div className="mt-6 p-5 bg-gray-50 border-2 border-gray-200 rounded-lg">
            <div className="flex items-start gap-3">
              <XCircle className="h-6 w-6 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-bold text-gray-900 mb-2">
                  Vos revenus dépassent les plafonds MaPrimeAdapt&apos;
                </p>
                <p className="text-gray-700">
                  Le plafond est de {formaterEuros(resultat.plafondModestes)} pour votre
                  foyer. D&apos;autres aides existent — caisse de retraite, aide
                  départementale, TVA réduite à 5,5 % — et ne dépendent pas des mêmes
                  conditions. Appelez-nous, le diagnostic reste gratuit.
                </p>
                <a
                  href="tel:0939035444"
                  className="inline-block mt-4 font-bold text-orange-700 hover:underline text-lg"
                >
                  09 39 03 54 44
                </a>
              </div>
            </div>
          </div>
        )}

        {resultat?.type === "eligible" && (
          <div className="mt-6 p-5 bg-green-50 border-2 border-green-300 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="w-full">
                <p className="font-bold text-green-900 text-lg mb-3">
                  Vous êtes éligible à MaPrimeAdapt&apos;
                </p>
                <p className="text-gray-900 mb-4">
                  Votre foyer relève des ménages{" "}
                  <strong>
                    {resultat.categorie === "tresModestes" ? "très modestes" : "modestes"}
                  </strong>{" "}
                  : l&apos;État prend en charge{" "}
                  <strong>{Math.round(resultat.taux * 100)} % du montant des travaux</strong>.
                </p>

                <div className="bg-white rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-gray-900">
                    <span>
                      {travauxEstimes ? "Travaux estimés" : "Travaux au plafond"}
                    </span>
                    <span className="font-medium">{formaterEuros(resultat.assiette)}</span>
                  </div>
                  <div className="flex justify-between text-green-800 font-bold text-lg">
                    <span>Aide MaPrimeAdapt&apos;</span>
                    <span>− {formaterEuros(resultat.aide)}</span>
                  </div>
                  <div className="flex justify-between text-gray-900 pt-2 border-t border-gray-200 font-bold">
                    <span>Reste à votre charge</span>
                    <span>{formaterEuros(resultat.resteACharge)}</span>
                  </div>
                </div>

                <p className="text-sm text-gray-700 mt-3">
                  Estimation indicative : le montant définitif dépend des travaux
                  retenus après la visite à domicile, et l&apos;aide est plafonnée à{" "}
                  {formaterEuros(PLAFOND_TRAVAUX)} de travaux HT.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-600 mt-6">
        Barème {ANNEE_BAREME} pour l&apos;Île-de-France. Source :{" "}
        <a
          href={SOURCE_BAREME.url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-900"
        >
          {SOURCE_BAREME.libelle}
        </a>
        .
      </p>
    </div>
  );
};

export default SimulateurRapide;
